// Machine Learning / Deep Learning interview questions data
export const mlQuestions = {
  'ml-fundamentals': [
    {
      id: 1,
      question: "What is the difference between supervised, unsupervised, and reinforcement learning?",
      answer: "Supervised learning uses examples where you already know the right answer (labels), like spam vs not spam (classification) or house prices (regression). Unsupervised learning only sees raw data and looks for structure, such as clusters or simpler representations. Reinforcement learning is an agent trying actions in an environment and learning from rewards or penalties."
    },
    {
      id: 2,
      question: "What is overfitting and underfitting?",
      answer: "Overfitting is when the model memorizes training noise: great on training data, bad on new data (high variance). Underfitting is when the model is too simple to learn the real pattern: poor on both train and test (high bias). You fight overfitting with more data, simpler models, or regularization."
    },
    {
      id: 3,
      question: "What is the bias-variance tradeoff?",
      answer: "Bias is systematic error from a model that is too simple (underfitting). Variance is error from being too sensitive to training quirks (overfitting). More complex models usually lower bias but raise variance. The goal is a balance where total error on new data is lowest."
    },
    {
      id: 4,
      question: "What is cross-validation?",
      answer: "Cross-validation splits data into k parts, trains on k-1, tests on the held-out part, and rotates so every row is tested once. K-fold cross-validation gives a steadier estimate of how the model generalizes than a single random split, which matters a lot when data is small."
    },
    {
      id: 5,
      question: "What is the difference between classification and regression?",
      answer: "Classification picks a category (spam or not, which animal). Regression predicts a number (price, temperature). They use different loss functions: cross-entropy for classification, MSE or MAE for regression."
    },
    {
      id: 6,
      question: "What is feature engineering?",
      answer: "Feature engineering means shaping raw inputs so the model sees useful signals: new columns, encoding categories, scaling numbers, or pulling out domain-specific facts. In classic ML, data quality and features often matter more than picking the fanciest algorithm."
    },
    {
      id: 7,
      question: "What is the difference between bagging and boosting?",
      answer: "Bagging trains many models on random subsamples of the data in parallel and averages their votes, which cuts variance (Random Forest is the famous example). Boosting trains models one after another, each focusing on previous mistakes, which cuts bias (XGBoost, AdaBoost). Boosting is often more accurate but easier to overfit if you are not careful."
    },
    {
      id: 8,
      question: "What is regularization?",
      answer: "Regularization adds a penalty to the loss so weights do not grow huge, which helps prevent overfitting. L1 (Lasso) can drive some weights to exactly zero, which acts like automatic feature selection. L2 (Ridge) shrinks weights smoothly but keeps all features. Elastic Net mixes L1 and L2."
    }
  ],

  'neural-networks': [
    {
      id: 1,
      question: "What is a neural network?",
      answer: "A neural network stacks layers that each do a weighted sum of inputs plus a bias, then apply a nonlinear activation. Many layers let the network build simple patterns into complex ones. Training means adjusting weights with gradient descent and backpropagation to lower a loss function."
    },
    {
      id: 2,
      question: "What is backpropagation?",
      answer: "Backpropagation applies the chain rule to send the error gradient from the output back through every layer so you know how much each weight contributed to the mistake. An optimizer (SGD, Adam) then nudges weights in the direction that reduces loss. Without backprop, deep networks would be impractical to train."
    },
    {
      id: 3,
      question: "What are activation functions and why are they needed?",
      answer: "Activations add nonlinearity. Without them, many linear layers would collapse into one linear layer. ReLU (max(0, x)) is the usual default: fast and helps avoid vanishing gradients in deep nets. Sigmoid maps to (0, 1) for binary outputs; Softmax turns logits into a probability vector over classes."
    },
    {
      id: 4,
      question: "What is the vanishing gradient problem?",
      answer: "In very deep nets, gradients can shrink toward zero as they flow backward through saturating activations like sigmoid or tanh, so early layers barely learn. Fixes include ReLU, batch normalization, residual (skip) connections in ResNets, and gated RNNs like LSTM."
    },
    {
      id: 5,
      question: "What is the exploding gradient problem?",
      answer: "Gradients can also blow up exponentially during backprop, causing huge weight updates and unstable training, especially in deep or recurrent models. Common fixes are gradient clipping, careful weight initialization, and batch normalization."
    },
    {
      id: 6,
      question: "What is dropout?",
      answer: "Dropout randomly turns off some neurons during training so the network cannot rely on a few paths. At test time all neurons are on, often with weights scaled to match. It acts like training many thinner networks and averaging them, which reduces overfitting."
    },
    {
      id: 7,
      question: "What is batch normalization?",
      answer: "Batch normalization rescales activations in a layer across the mini-batch to roughly zero mean and unit variance, then applies learnable scale and shift. That stabilizes training, allows higher learning rates, and reduces sensitivity to bad initialization, with a small regularizing side effect."
    },
    {
      id: 8,
      question: "Difference between batch gradient descent, mini-batch, and stochastic gradient descent.",
      answer: "Batch gradient descent uses the entire dataset each update: smooth but slow. Stochastic gradient descent (SGD) uses one example per step: very noisy but cheap per step. Mini-batch is the practical middle ground (often 32 to 256 examples): noisy enough for speed, stable enough to converge, and friendly to GPUs."
    }
  ],

  'cnn': [
    {
      id: 1,
      question: "What is a Convolutional Neural Network (CNN)?",
      answer: "A CNN is built for grid data like images. Convolution layers slide small learned filters over the input to detect local patterns (edges, textures, parts). Shared weights across space mean far fewer parameters than a fully connected layer on every pixel, and similar patterns are found anywhere in the image."
    },
    {
      id: 2,
      question: "How does a convolutional layer work?",
      answer: "A small filter (kernel) moves across the input, multiplying overlapping patches and summing into one number per position to build a feature map. Many filters learn different patterns. Output size depends on kernel size, stride, and padding."
    },
    {
      id: 3,
      question: "What is pooling in a CNN?",
      answer: "Pooling shrinks the height and width of feature maps to save compute and add a little tolerance to small shifts. Max pooling keeps the strongest signal in each window; average pooling averages the window. Max pooling is more common in vision CNNs."
    },
    {
      id: 4,
      question: "What is the receptive field of a neuron?",
      answer: "The receptive field is the part of the original input that can influence one neuron’s output. Deeper layers stack receptive fields, so neurons see larger pieces of the image and capture more global context."
    },
    {
      id: 5,
      question: "Why do we use convolutions instead of fully connected layers for images?",
      answer: "Images have local structure and translation: a cat in the corner uses the same edges as a cat in the center. Convolutions reuse one filter everywhere and keep spatial layout, so parameter count stays reasonable. A naive fully connected layer on every pixel would need an enormous weight matrix."
    },
    {
      id: 6,
      question: "What is stride and padding in a CNN?",
      answer: "Stride is how many pixels the filter moves each step; larger stride means smaller output maps. Padding adds zeros around the border so you can control size: 'same' padding often keeps spatial dimensions stable; 'valid' padding uses no extra border and shrinks the map."
    },
    {
      id: 7,
      question: "What is transfer learning?",
      answer: "Transfer learning starts from a model pretrained on a large dataset (for example ImageNet with ResNet) and fine-tunes the last layers (or all layers) on your smaller task. Early layers already know generic features like edges, so you need less data and time than training from scratch."
    }
  ],

  'rnn-transformers': [
    {
      id: 1,
      question: "What is an RNN and what problem does it solve?",
      answer: "A Recurrent Neural Network (RNN) processes sequences (text, time series) one step at a time while carrying a hidden state forward so past inputs affect the present. That handles variable-length sequences where a plain feedforward net would not. Training unfolds the sequence with Backpropagation Through Time (BPTT)."
    },
    {
      id: 2,
      question: "What is an LSTM and how does it fix the vanishing gradient problem?",
      answer: "LSTM (Long Short-Term Memory) adds a cell state plus input, forget, and output gates so the network can keep or drop information across many steps. The cell state gives gradients a smoother path, which reduces the vanishing gradient problem compared to a basic RNN."
    },
    {
      id: 3,
      question: "What is the attention mechanism?",
      answer: "Attention lets the model look back at all input positions and weight them when producing each output, instead of squeezing the whole input into one fixed vector. It computes a weighted sum of values where weights come from how well query matches keys. That removed a big bottleneck in old encoder-decoder RNNs."
    },
    {
      id: 4,
      question: "What is self-attention?",
      answer: "Self-attention lets every token in one sequence attend to every other token in that same sequence. Query, key, and value all come from the same layer input. All positions can be updated in parallel (unlike step-by-step RNNs), which scales well on GPUs and captures long-range links."
    },
    {
      id: 5,
      question: "What is the difference between encoder-only, decoder-only, and encoder-decoder Transformers?",
      answer: "Encoder-only models like BERT read the full input in both directions and are strong for classification and embeddings. Decoder-only models like GPT and Llama generate text left to right (autoregressive) and power most chat LLMs. Encoder-decoder models like T5 and BART map an input sequence to an output sequence (translation, summarization)."
    }
  ],

  'training-optimization': [
    {
      id: 1,
      question: "What is the learning rate and why is it important?",
      answer: "The learning rate sets how big each weight update is after you compute gradients. Too large and training diverges or bounces; too small and training crawls or stalls. Schedulers (warmup, cosine decay, ReduceLROnPlateau) change the rate during training to help convergence."
    },
    {
      id: 2,
      question: "What is the Adam optimizer?",
      answer: "Adam (Adaptive Moment Estimation) keeps a moving average of gradients and squared gradients per parameter so each weight gets its own effective step size. It blends ideas from momentum and RMSprop and usually converges faster than plain SGD without manual tuning of one global rate per weight."
    },
    {
      id: 3,
      question: "What is early stopping?",
      answer: "Early stopping watches validation loss each epoch and stops training when it stops improving for a set number of epochs (patience). That cuts off overfitting without extra math in the model and is simple and effective."
    },
    {
      id: 4,
      question: "What is data augmentation?",
      answer: "Data augmentation creates more training variety by randomly transforming inputs: flips, crops, color jitter for images; paraphrases or noise for text. The model sees more diversity without collecting new labels, which often improves test performance."
    },
    {
      id: 5,
      question: "What is weight initialization and why does it matter?",
      answer: "Initialization sets starting weights before the first gradient step. Bad starts (all zeros, huge values) cause vanishing or exploding signals. He init fits ReLU nets; Xavier (Glorot) init fits sigmoid or tanh. Good init keeps activations and gradients in a healthy range early in training."
    },
    {
      id: 6,
      question: "What is a loss function?",
      answer: "The loss function scores how wrong predictions are compared to true labels; training minimizes this number. Binary cross-entropy for two-class problems, categorical cross-entropy for multi-class, MSE for regression. Pick the loss that matches your task or you optimize the wrong objective."
    }
  ],

  'evaluation-metrics': [
    {
      id: 1,
      question: "What is the difference between accuracy, precision, recall, and F1?",
      answer: "Accuracy is the fraction of all predictions that are correct; it hides mistakes when classes are imbalanced. Precision is: among predicted positives, how many were truly positive. Recall is: among real positives, how many did we catch. F1 is the harmonic mean of precision and recall and is a standard single score when classes are skewed."
    },
    {
      id: 2,
      question: "What is a confusion matrix?",
      answer: "A confusion matrix counts true positives, true negatives, false positives, and false negatives. It shows whether errors are mostly false alarms (hurts precision) or missed positives (hurts recall) so you can tune thresholds or the model accordingly."
    },
    {
      id: 3,
      question: "What is the ROC curve and AUC?",
      answer: "The ROC curve plots true positive rate against false positive rate as you change the classification threshold. AUC is the area under that curve: 1 is perfect separation, 0.5 is random guessing. It summarizes how well the model ranks positives over negatives across all thresholds."
    },
    {
      id: 4,
      question: "What is the difference between macro, micro, and weighted average metrics?",
      answer: "Macro averages the metric per class with equal weight, so rare classes count as much as common ones. Micro pools all examples first, so frequent classes dominate. Weighted macro averages by class frequency (support), which matches many real-world reporting needs on imbalanced data."
    },
    {
      id: 5,
      question: "What is mean squared error (MSE) vs mean absolute error (MAE)?",
      answer: "MSE squares errors so big mistakes dominate the average; use when large errors are especially costly. MAE uses absolute value so all errors contribute equally and outliers matter less. RMSE is the square root of MSE, so it lives in the same units as the target and is common to report."
    }
  ],

  'advanced': [
    {
      id: 1,
      question: "What is the difference between a generative and a discriminative model?",
      answer: "A discriminative model learns the boundary between classes directly, modeling P(y|x); examples include logistic regression, SVM, and CNN classifiers. A generative model tries to learn how data itself is distributed, P(x), and can sample new points; examples include GANs, VAEs, and diffusion models. Generative models are more flexible but often harder to train stably."
    },
    {
      id: 2,
      question: "What is a GAN?",
      answer: "A GAN pairs a generator that fakes data with a discriminator that guesses real vs fake. They train in opposition: the generator tries to fool the discriminator, the discriminator tries not to be fooled. GANs can make very realistic images but training can be unstable (mode collapse and oscillation)."
    },
    {
      id: 3,
      question: "What is a VAE?",
      answer: "A VAE (Variational Autoencoder) maps input to a distribution in latent space, samples from it, and decodes back to data. Training minimizes reconstruction error plus a KL term that keeps the latent distribution smooth and organized. VAEs are usually easier to train than GANs but outputs can be blurrier."
    },
    {
      id: 4,
      question: "What is PCA?",
      answer: "PCA (Principal Component Analysis) finds the directions in data with the largest spread (principal components) and projects points onto fewer of those directions. It reduces dimension for visualization, denoising, or speeding up later models."
    },
    {
      id: 5,
      question: "What is the curse of dimensionality?",
      answer: "As you add more features, the volume of the space grows fast while your sample count does not, so data look sparse and distances between points become less meaningful. That hurts KNN and many clustering methods. People respond with fewer features, PCA, or learned embeddings."
    }
  ]
};

// Helper to get all questions
export const getAllMlQuestions = () => {
  const allQuestions = [];
  Object.keys(mlQuestions).forEach(topic => {
    mlQuestions[topic].forEach(q => {
      allQuestions.push({ ...q, topic });
    });
  });
  return allQuestions;
};

// Shuffle helper
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
